import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Upload, X, FileText, GitPullRequest, Plus, Search, Award } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, API } from "../lib/api";
import { STATUSES, NOTE_TYPES, DOC_TYPES } from "../lib/constants";
import { StatusBadge, AwaitingBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const EMPTY = {
  name: "", category_ids: [], subcategory_ids: [], description: "", address: "",
  city: "", zip: "", phones: [], email: "", website: "", business_hours: {}, service_cities: [], status: "draft",
};

const DAYS = [
  { key: "sunday", label: "Sunday" },
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
];

export default function ListingForm() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [listing, setListing] = useState(null);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [note, setNote] = useState({ note_type: "call_log", content: "" });
  const [docType, setDocType] = useState("business_registration");
  const [saving, setSaving] = useState(false);
  const [requestMode, setRequestMode] = useState(false);
  const [catReq, setCatReq] = useState({ name: "", parent_id: "", reason: "" });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/cities").then((r) => setCities(r.data));
    if (!isNew) {
      api.get(`/listings/${id}`).then((r) => {
        setListing(r.data);
        setForm({
          name: r.data.name, category_ids: r.data.category_ids || [], subcategory_ids: r.data.subcategory_ids || [],
          description: r.data.description || "", address: r.data.address || "", city: r.data.city || "",
          zip: r.data.zip || "", phones: r.data.phones || [], email: r.data.email || "", website: r.data.website || "",
          business_hours: r.data.business_hours || {}, service_cities: r.data.service_cities || [], status: r.data.status,
        });
      }).catch((e) => { toast.error(errMsg(e)); navigate("/admin/listings"); });
      loadNotes();
    }
  }, [id]);

  const loadNotes = () => api.get(`/listings/${id}/notes`).then((r) => setNotes(r.data)).catch(() => {});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCity = (name, checked) =>
    set("service_cities", checked ? [...form.service_cities, name] : form.service_cities.filter((c) => c !== name));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      payload.phones = payload.phones.filter((p) => p.trim());
      if (requestMode) {
        payload.category_ids = [];
        payload.subcategory_ids = [];
        payload.category_request = { name: catReq.name, parent_id: catReq.parent_id || null, reason: catReq.reason };
      }
      if (isNew) {
        const r = await api.post("/listings", payload);
        toast.success(requestMode ? "Listing saved as draft — category request sent for admin approval" : "Listing created");
        navigate(`/admin/listings/${r.data.id}`);
      } else {
        await api.put(`/listings/${id}`, payload);
        toast.success("Listing updated");
        const r = await api.get(`/listings/${id}`);
        setListing(r.data);
      }
    } catch (e2) { toast.error(errMsg(e2)); } finally { setSaving(false); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.content.trim()) return;
    try {
      await api.post(`/listings/${id}/notes`, note);
      setNote({ note_type: "call_log", content: "" });
      loadNotes();
      toast.success("Note added");
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/listings/${id}/images`, fd);
      const r = await api.get(`/listings/${id}`);
      setListing(r.data);
      toast.success("Image uploaded");
    } catch (e2) { toast.error(errMsg(e2)); }
    e.target.value = "";
  };

  const removeImage = async (path) => {
    try {
      await api.delete(`/listings/${id}/images`, { params: { path } });
      const r = await api.get(`/listings/${id}`);
      setListing(r.data);
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const refreshListing = () => api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => {});

  const uploadBrand = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/listings/${id}/${kind}`, fd);
      refreshListing();
      toast.success(kind === "logo" ? "Logo uploaded" : "Award logo added");
    } catch (e2) { toast.error(errMsg(e2)); }
    e.target.value = "";
  };

  const removeLogo = async () => {
    try {
      await api.delete(`/listings/${id}/logo`);
      refreshListing();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const removeAward = async (path) => {
    try {
      await api.delete(`/listings/${id}/award-logos`, { params: { path } });
      refreshListing();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", docType);
    try {
      await api.post(`/listings/${id}/documents`, fd);
      const r = await api.get(`/listings/${id}`);
      setListing(r.data);
      loadNotes();
      toast.success("Document uploaded for review");
    } catch (e2) { toast.error(errMsg(e2)); }
    e.target.value = "";
  };

  const field = (label, key, props = {}) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input data-testid={`field-${key}`} value={form[key]} onChange={(e) => set(key, e.target.value)} {...props} />
    </div>
  );

  return (
    <div data-testid="listing-form-page" className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" data-testid="back-to-listings" onClick={() => navigate("/admin/listings")} className="border-[#E2E8F0]">
          <ArrowLeft size={15} />
        </Button>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold tracking-tight text-[#1B2A4A]">{isNew ? "Add Business" : form.name}</h1>
          {!isNew && <StatusBadge status={listing?.status} />}
          {!isNew && listing?.awaiting_category && <AwaitingBadge />}
        </div>
      </div>

      {isNew ? (
        <DetailsForm
          form={form} set={set} field={field} categories={categories} cities={cities}
          toggleCity={toggleCity} save={save} saving={saving} isNew={isNew}
          requestMode={requestMode} setRequestMode={setRequestMode} catReq={catReq} setCatReq={setCatReq}
        />
      ) : (
        <Tabs defaultValue="details">
          <TabsList className="bg-white border border-[#E2E8F0]">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes & Call Log</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="images" data-testid="tab-images">Images</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-6">
            <DetailsForm
              form={form} set={set} field={field} categories={categories} cities={cities}
              toggleCity={toggleCity} save={save} saving={saving} isNew={isNew}
              awaiting={listing?.awaiting_category} catRequest={listing?.category_request}
            />
          </TabsContent>

          <TabsContent value="notes" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <form onSubmit={addNote} className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3 h-fit">
                <h3 className="font-semibold text-[#1B2A4A]">Add Note</h3>
                <Select value={note.note_type} onValueChange={(v) => setNote((n) => ({ ...n, note_type: v }))}>
                  <SelectTrigger data-testid="note-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {NOTE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea data-testid="note-content-input" placeholder="Called on 6/1, left voicemail..." value={note.content} onChange={(e) => setNote((n) => ({ ...n, content: e.target.value }))} rows={4} />
                <Button data-testid="add-note-button" type="submit" className="bg-[#2E7BD6] hover:bg-[#2568b8] text-white w-full">Add Note</Button>
              </form>
              <div className="lg:col-span-2 space-y-3" data-testid="notes-timeline">
                {notes.length === 0 && <div className="text-sm text-[#94A3B8] bg-white border border-[#E2E8F0] rounded-xl p-5">No notes yet. Log outreach attempts here.</div>}
                {notes.map((n) => (
                  <div key={n.id} data-testid={`note-${n.id}`} className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-[#2E7BD6]">
                          {NOTE_TYPES.find((t) => t.value === n.note_type)?.label || n.note_type}
                        </span>
                        <span className="text-xs text-[#94A3B8]">by {n.employee_name}</span>
                      </div>
                      <span className="text-xs text-[#94A3B8]">{new Date(n.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{n.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-6">
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4">
              <div className="flex items-end gap-3 flex-wrap">
                <div className="space-y-1.5">
                  <Label>Document Type</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger data-testid="doc-type-select" className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <input type="file" id="doc-upload" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={uploadDoc} data-testid="doc-upload-input" />
                  <Button type="button" data-testid="upload-doc-button" onClick={() => document.getElementById("doc-upload").click()} className="bg-[#F5961B] hover:bg-[#dd8512] text-white">
                    <Upload size={15} className="mr-2" /> Upload Document
                  </Button>
                </div>
              </div>
              <div className="space-y-2" data-testid="documents-list">
                {(listing?.documents || []).length === 0 && <div className="text-sm text-[#94A3B8]">No verification documents uploaded.</div>}
                {(listing?.documents || []).map((d) => (
                  <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-3 border border-[#F1F5F9] rounded-lg px-4 py-3">
                    <FileText size={18} className="text-[#2E7BD6]" />
                    <div className="flex-1 min-w-0">
                      <a href={`${API}/files/${d.storage_path}`} target="_blank" rel="noreferrer" data-testid={`doc-link-${d.id}`} className="text-sm font-medium text-[#2E7BD6] hover:underline truncate block">
                        {d.original_filename}
                      </a>
                      <div className="text-xs text-[#94A3B8]">{DOC_TYPES.find((t) => t.value === d.document_type)?.label} · uploaded by {d.uploaded_by} · {new Date(d.created_at).toLocaleDateString()}</div>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${
                      d.status === "approved" ? "text-[#047857] bg-[#D1FAE5] border-[#6EE7B7]" :
                      d.status === "rejected" ? "text-[#B91C1C] bg-[#FEE2E2] border-[#FECACA]" :
                      "text-[#B45309] bg-[#FEF3C7] border-[#FDE68A]"
                    }`} data-testid={`doc-status-${d.id}`}>{d.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="images" className="mt-6">
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-semibold text-[#1B2A4A]">Company Logo</h4>
                  <input type="file" id="logo-upload" className="hidden" accept="image/*" onChange={(e) => uploadBrand(e, "logo")} data-testid="logo-upload-input" />
                  {listing?.logo ? (
                    <div className="relative group w-40 h-40 rounded-xl border border-[#E2E8F0] bg-[#F5F7FA] flex items-center justify-center overflow-hidden">
                      <img src={`${API}/files/${listing.logo}`} alt="Company logo" className="max-w-full max-h-full object-contain" data-testid="company-logo-preview" />
                      <button type="button" data-testid="remove-logo-button" onClick={removeLogo} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button type="button" data-testid="upload-logo-button" onClick={() => document.getElementById("logo-upload").click()} className="w-40 h-40 rounded-xl border-2 border-dashed border-[#E2E8F0] hover:border-[#2E7BD6]/50 hover:bg-[#F8FAFC] transition-colors flex flex-col items-center justify-center gap-2 text-[#94A3B8]">
                      <Upload size={20} />
                      <span className="text-xs font-medium">Upload logo</span>
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  <h4 className="font-semibold text-[#1B2A4A]">Award Logos & Badges</h4>
                  <input type="file" id="award-upload" className="hidden" accept="image/*" onChange={(e) => uploadBrand(e, "award-logos")} data-testid="award-upload-input" />
                  <div className="flex flex-wrap gap-3" data-testid="award-logos-grid">
                    {(listing?.award_logos || []).map((p) => (
                      <div key={p} className="relative group w-20 h-20 rounded-xl border border-[#E2E8F0] bg-[#F5F7FA] flex items-center justify-center overflow-hidden">
                        <img src={`${API}/files/${p}`} alt="Award" className="max-w-full max-h-full object-contain" data-testid={`award-logo-${p.split("/").pop()}`} />
                        <button type="button" data-testid={`remove-award-${p.split("/").pop()}`} onClick={() => removeAward(p)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <X size={11} />
                        </button>
                      </div>
                    ))}
                    <button type="button" data-testid="add-award-button" onClick={() => document.getElementById("award-upload").click()} className="w-20 h-20 rounded-xl border-2 border-dashed border-[#E2E8F0] hover:border-[#F5961B]/60 hover:bg-[#FDF0DC]/40 transition-colors flex flex-col items-center justify-center gap-1 text-[#94A3B8]">
                      <Award size={16} />
                      <span className="text-[10px] font-medium">Add award</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="border-t border-[#F1F5F9] pt-5 space-y-4">
              <div>
                <input type="file" id="img-upload" className="hidden" accept="image/*" onChange={uploadImage} data-testid="image-upload-input" />
                <h4 className="font-semibold text-[#1B2A4A] mb-3">Business Photos</h4>
                <Button type="button" data-testid="upload-image-button" onClick={() => document.getElementById("img-upload").click()} className="bg-[#2E7BD6] hover:bg-[#2568b8] text-white">
                  <Upload size={15} className="mr-2" /> Upload Image
                </Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="images-grid">
                {(listing?.images || []).length === 0 && <div className="text-sm text-[#94A3B8] col-span-full">No images yet.</div>}
                {(listing?.images || []).map((p) => (
                  <div key={p} className="relative group rounded-xl overflow-hidden border border-[#E2E8F0] aspect-video bg-[#F5F7FA]">
                    <img src={`${API}/files/${p}`} alt="" className="w-full h-full object-cover" data-testid={`image-${p.split("/").pop()}`} />
                    <button
                      type="button"
                      data-testid={`remove-image-${p.split("/").pop()}`}
                      onClick={() => removeImage(p)}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function DetailsForm({ form, set, field, categories, cities, toggleCity, save, saving, isNew, requestMode, setRequestMode, catReq, setCatReq, awaiting, catRequest }) {
  const topCats = categories.filter((c) => !c.parent_id);
  const addCat = (cid) => { if (cid && !form.category_ids.includes(cid)) set("category_ids", [...form.category_ids, cid]); };
  const removeCat = (cid) => {
    const subIds = categories.filter((c) => c.parent_id === cid).map((c) => c.id);
    set("subcategory_ids", form.subcategory_ids.filter((s) => !subIds.includes(s)));
    set("category_ids", form.category_ids.filter((x) => x !== cid));
  };
  const toggleSub = (sid) =>
    set("subcategory_ids", form.subcategory_ids.includes(sid)
      ? form.subcategory_ids.filter((x) => x !== sid)
      : [...form.subcategory_ids, sid]);

  const [serviceQuery, setServiceQuery] = useState("");
  const subResults = serviceQuery.trim()
    ? categories.filter((c) => c.parent_id && c.name.toLowerCase().includes(serviceQuery.trim().toLowerCase())).slice(0, 10)
    : [];
  const pickService = (sub) => {
    if (!form.category_ids.includes(sub.parent_id)) set("category_ids", [...form.category_ids, sub.parent_id]);
    if (!form.subcategory_ids.includes(sub.id)) set("subcategory_ids", [...form.subcategory_ids, sub.id]);
    setServiceQuery("");
  };

  const setDay = (day, patch) =>
    set("business_hours", { ...form.business_hours, [day]: { open: false, from: "", to: "", ...(form.business_hours[day] || {}), ...patch } });
  return (
    <form onSubmit={save} className="space-y-6">
      {awaiting && catRequest && (
        <div data-testid="awaiting-category-notice" className="flex items-start gap-3 bg-[#FDF0DC] border border-[#F5961B] rounded-xl p-4">
          <GitPullRequest size={18} className="text-[#F5961B] mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-[#1B2A4A]">Awaiting category approval</div>
            <div className="text-[#475569]">
              Requested category <strong>"{catRequest.name}"</strong> is pending admin review ({catRequest.status}). The listing goes live once approved.
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-5">
        <h3 className="text-lg font-semibold text-[#1B2A4A]">Business Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">{field("Business Name *", "name", { required: true, placeholder: "ABC Plumbing Services" })}</div>

          {isNew && (
            <div className="md:col-span-2 flex items-center gap-2">
              <Checkbox
                id="request-mode"
                data-testid="request-category-toggle"
                checked={requestMode}
                onCheckedChange={setRequestMode}
              />
              <Label htmlFor="request-mode" className="text-sm font-normal text-[#475569] cursor-pointer">
                Category not available? Request a new one (listing stays in draft until the super admin approves)
              </Label>
            </div>
          )}

          {isNew && requestMode ? (
            <>
              <div className="space-y-1.5">
                <Label>Requested Category Name *</Label>
                <Input data-testid="field-requested-category" required value={catReq.name} onChange={(e) => setCatReq((c) => ({ ...c, name: e.target.value }))} placeholder="e.g. Solar Panel Cleaning" />
              </div>
              <div className="space-y-1.5">
                <Label>Parent Category (optional)</Label>
                <Select value={catReq.parent_id || "none"} onValueChange={(v) => setCatReq((c) => ({ ...c, parent_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="field-request-parent"><SelectValue placeholder="None (top-level)" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">None (top-level)</SelectItem>
                    {topCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label>Reason / Notes for Admin</Label>
                <Textarea data-testid="field-request-reason" value={catReq.reason} onChange={(e) => setCatReq((c) => ({ ...c, reason: e.target.value }))} rows={2} placeholder="Why this category is needed..." />
              </div>
            </>
          ) : (
            <div className="md:col-span-2 space-y-3">
              <Label>
                Categories *{" "}
                <span className="font-normal text-[#94A3B8] text-xs">(select all that apply — e.g. Remodeling, Painting, Plumbing)</span>
              </Label>
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] z-10" />
                <Input
                  data-testid="service-search-input"
                  value={serviceQuery}
                  onChange={(e) => setServiceQuery(e.target.value)}
                  placeholder="Search a service (e.g. Painting, Plumbing) — picking one auto-selects its category"
                  className="pl-9"
                />
                {serviceQuery.trim() && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-[#E2E8F0] rounded-xl shadow-lg max-h-64 overflow-y-auto" data-testid="service-search-results">
                    {subResults.length === 0 && (
                      <div className="px-4 py-3 text-sm text-[#94A3B8]">No matching service — use "Request a new category" below.</div>
                    )}
                    {subResults.map((s) => {
                      const parent = categories.find((c) => c.id === s.parent_id);
                      const added = form.subcategory_ids.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`service-result-${s.id}`}
                          disabled={added}
                          onClick={() => pickService(s)}
                          className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F5F7FA] flex items-center justify-between gap-3 transition-colors ${added ? "opacity-50" : ""}`}
                        >
                          <span className="font-medium text-[#0F172A]">{s.name}</span>
                          <span className="text-xs text-[#94A3B8]">{added ? "Added" : parent?.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {form.category_ids.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="selected-categories">
                  {form.category_ids.map((cid) => {
                    const cat = categories.find((c) => c.id === cid);
                    if (!cat) return null;
                    return (
                      <span key={cid} data-testid={`category-pill-${cid}`} className="inline-flex items-center gap-1.5 bg-[#1B2A4A] text-white text-sm font-medium rounded-full pl-3 pr-1.5 py-1">
                        {cat.name}
                        <button type="button" data-testid={`remove-category-${cid}`} onClick={() => removeCat(cid)} className="w-5 h-5 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition-colors">
                          <X size={11} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              {/* key forces the Select to reset to placeholder after each pick */}
              <Select key={form.category_ids.join(",")} value="" onValueChange={addCat}>
                <SelectTrigger data-testid="field-category-add" className="w-full md:w-96"><SelectValue placeholder="+ Add a category" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {topCats.filter((c) => !form.category_ids.includes(c.id)).map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`category-option-${c.id}`}>{c.group} — {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.category_ids.map((cid) => {
                const subs = categories.filter((c) => c.parent_id === cid);
                const cat = categories.find((c) => c.id === cid);
                if (!subs.length || !cat) return null;
                return (
                  <div key={cid} className="border border-[#E2E8F0] rounded-xl p-3.5 bg-[#F8FAFC]" data-testid={`subcategory-panel-${cid}`}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#475569] mb-2">{cat.name} — subcategories</div>
                    <div className="flex flex-wrap gap-1.5">
                      {subs.map((s) => {
                        const active = form.subcategory_ids.includes(s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            data-testid={`subcategory-chip-${s.id}`}
                            onClick={() => toggleSub(s.id)}
                            className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${
                              active ? "bg-[#2E7BD6] text-white border-[#2E7BD6]" : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#2E7BD6]/50"
                            }`}
                          >
                            {s.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="md:col-span-2 space-y-1.5">
            <Label>Description</Label>
            <Textarea data-testid="field-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} placeholder="Services offered, years in business, specialties..." />
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-5">
        <h3 className="text-lg font-semibold text-[#1B2A4A]">Contact & Location</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">{field("Street Address", "address", { placeholder: "123 Main St" })}</div>
          <div className="space-y-1.5">
            <Label>Home City (Broward)</Label>
            <Select value={form.city || "none"} onValueChange={(v) => set("city", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="field-city"><SelectValue placeholder="Select city" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="none">Select city</SelectItem>
                {cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {field("Zip Code", "zip", { placeholder: "33301" })}
          <div className="md:col-span-2 space-y-2">
            <Label>Phone Numbers</Label>
            {form.phones.map((p, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  data-testid={`field-phone-${i}`}
                  value={p}
                  placeholder="954-555-0100"
                  onChange={(e) => set("phones", form.phones.map((x, xi) => (xi === i ? e.target.value : x)))}
                />
                <Button type="button" variant="outline" size="icon" data-testid={`remove-phone-${i}`} onClick={() => set("phones", form.phones.filter((_, xi) => xi !== i))} className="border-[#E2E8F0] shrink-0">
                  <X size={14} />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" data-testid="add-phone-button" onClick={() => set("phones", [...form.phones, ""])} className="border-[#E2E8F0]">
              <Plus size={14} className="mr-1.5" /> Add phone number
            </Button>
          </div>
          {field("Email", "email", { type: "email", placeholder: "info@business.com" })}
          {field("Website", "website", { placeholder: "https://..." })}
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-[#1B2A4A]">Business Hours</h3>
          <p className="text-sm text-[#475569]">Let customers know when the business is open</p>
        </div>
        <div className="space-y-3" data-testid="business-hours-editor">
          {DAYS.map((d) => {
            const h = { open: false, from: "", to: "", ...(form.business_hours[d.key] || {}) };
            return (
              <div key={d.key} className="flex items-center gap-4 flex-wrap" data-testid={`hours-row-${d.key}`}>
                <span className="w-24 text-sm font-medium text-[#0F172A]">{d.label}</span>
                <div className="flex items-center gap-2">
                  <Switch data-testid={`hours-toggle-${d.key}`} checked={h.open} onCheckedChange={(v) => setDay(d.key, { open: v })} />
                  <span className="text-sm text-[#475569] w-14">{h.open ? "Open" : "Closed"}</span>
                </div>
                {h.open && (
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      data-testid={`hours-open-${d.key}`}
                      value={h.from}
                      onChange={(e) => setDay(d.key, { from: e.target.value })}
                      className="h-9 rounded-lg border border-[#E2E8F0] bg-white px-2 text-sm text-[#0F172A] outline-none focus:border-[#2E7BD6] focus:ring-2 focus:ring-[#2E7BD6]/20"
                    />
                    <span className="text-[#94A3B8] text-sm">—</span>
                    <input
                      type="time"
                      data-testid={`hours-close-${d.key}`}
                      value={h.to}
                      onChange={(e) => setDay(d.key, { to: e.target.value })}
                      className="h-9 rounded-lg border border-[#E2E8F0] bg-white px-2 text-sm text-[#0F172A] outline-none focus:border-[#2E7BD6] focus:ring-2 focus:ring-[#2E7BD6]/20"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-[#1B2A4A]">Service Areas</h3>
            <p className="text-sm text-[#475569]">Broward County cities this business serves ({form.service_cities.length} selected)</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" data-testid="select-all-cities" onClick={() => set("service_cities", cities.map((c) => c.name))} className="border-[#E2E8F0]">Select all</Button>
            <Button type="button" size="sm" variant="outline" data-testid="clear-all-cities" onClick={() => set("service_cities", [])} className="border-[#E2E8F0]">Clear</Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="service-cities-grid">
          {cities.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm text-[#0F172A] cursor-pointer rounded-lg border border-[#F1F5F9] px-3 py-2 hover:border-[#2E7BD6]/40 transition-colors">
              <Checkbox
                data-testid={`city-check-${c.slug}`}
                checked={form.service_cities.includes(c.name)}
                onCheckedChange={(checked) => toggleCity(c.name, checked)}
              />
              {c.name}
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 flex items-center justify-between flex-wrap gap-4">
        <div className="space-y-1.5">
          <Label>Pipeline Status</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)} disabled={awaiting}>
            <SelectTrigger data-testid="field-status" className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="save-listing-button" type="submit" disabled={saving} className="bg-[#F5961B] hover:bg-[#dd8512] text-white px-8 h-10">
          {saving ? "Saving..." : isNew ? "Create Listing" : "Save Changes"}
        </Button>
      </div>
    </form>
  );
}
